import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-location';
import { OfficeHeader, Paginator, TableComponent, TableLoader, toast } from 'components';
import { usePagination } from 'hooks';
import { classNames, useUrlState } from 'utils';
import { useAppNaviagte } from '_graphql/cache/auth';
import { SUPPLIER_INBOX } from '_graphql/queries/inbox';
import { ATTEND_INBOX_ITEM } from '_graphql/mutation/inbox';
import {
  InboxKind,
  InboxStatus,
} from '_graphql/__generated__/globalTypes';
import {
  SupplierInbox,
  SupplierInbox_supplierInbox,
  SupplierInboxVariables,
} from '_graphql/queries/__generated__/SupplierInbox';
import {
  AttendInboxItem,
  AttendInboxItemVariables,
} from '_graphql/mutation/__generated__/AttendInboxItem';
import { useSupplierInbox } from './InboxProvider';
import { inboxColumns, kindFilterOptions } from './columns';
import { inboxDestination } from './deepLink';
import { OPEN_INBOX_VARIABLES } from './applyInboxEvent';

const InboxPage = () => {
  const navigate = useAppNaviagte();
  const { items, count, loading, refetch } = useSupplierInbox();
  const [activeTab, setActiveTab] = useUrlState('activeTab');
  const [kind, setKind] = useUrlState('kind');
  const { offset, limit, setPage } = usePagination();
  const tab = activeTab === 'attended' ? 'attended' : 'open';
  const kindFilter =
    kind && Object.values(InboxKind).includes(kind as InboxKind)
      ? (kind as InboxKind)
      : undefined;

  const attendedFilter = useMemo(
    () => ({
      status: InboxStatus.ATTENDED,
      ...(kindFilter ? { kind: kindFilter } : {}),
    }),
    [kindFilter],
  );

  const {
    data: attendedData,
    loading: attendedLoading,
    refetch: refetchAttended,
  } = useQuery<SupplierInbox, SupplierInboxVariables>(SUPPLIER_INBOX, {
    skip: tab !== 'attended',
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: attendedFilter,
      pagination: { limit, offset },
    },
  });

  const [attend, { loading: attending }] = useMutation<
    AttendInboxItem,
    AttendInboxItemVariables
  >(ATTEND_INBOX_ITEM);
  const [attendingId, setAttendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTab) setActiveTab('open');
  }, [activeTab, setActiveTab]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, kindFilter]);

  const openItems = useMemo(() => {
    const filtered = kindFilter
      ? items.filter((row) => row.kind === kindFilter)
      : items;
    return filtered.slice(offset, offset + limit);
  }, [items, kindFilter, offset, limit]);

  const openTotal = kindFilter
    ? items.filter((row) => row.kind === kindFilter).length
    : count;

  const rows: SupplierInbox_supplierInbox[] =
    tab === 'attended' ? attendedData?.supplierInbox ?? [] : openItems;
  const total =
    tab === 'attended' ? attendedData?.supplierInboxCount ?? 0 : openTotal;
  const tableLoading = tab === 'attended' ? attendedLoading : loading;

  const openItem = useCallback(
    (item: SupplierInbox_supplierInbox) => {
      const dest = inboxDestination(item);
      navigate({
        to: dest.to,
        search: dest.search ?? ((prev) => ({ ...prev })),
      });
    },
    [navigate],
  );

  const markDone = useCallback(
    async (item: SupplierInbox_supplierInbox) => {
      setAttendingId(item.id);
      try {
        await attend({
          variables: { id: item.id },
          update(cache) {
            cache.updateQuery<SupplierInbox, SupplierInboxVariables>(
              { query: SUPPLIER_INBOX, variables: OPEN_INBOX_VARIABLES },
              (prev) => {
                if (!prev) return prev;
                const existed = prev.supplierInbox.some((row) => row.id === item.id);
                return {
                  ...prev,
                  supplierInbox: prev.supplierInbox.filter((row) => row.id !== item.id),
                  supplierInboxCount: existed
                    ? Math.max(0, prev.supplierInboxCount - 1)
                    : prev.supplierInboxCount,
                };
              },
            );
          },
        });
      } catch (err) {
        toast(
          err instanceof Error ? err.message : 'Could not mark inbox item done',
          'error',
        );
      } finally {
        setAttendingId(null);
      }
    },
    [attend],
  );

  const columns = useMemo(
    () =>
      inboxColumns({
        onOpen: openItem,
        onAttend: tab === 'open' ? markDone : undefined,
        attendingId: attending ? attendingId : null,
        attended: tab === 'attended',
      }),
    [tab, attending, attendingId, openItem, markDone],
  );

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <OfficeHeader />
      <div className="block">
        <div className="border-b border-gray-200 bg-white px-6">
          <nav className="-mb-px flex space-x-8 items-center" aria-label="Tabs">
            <Link
              search={(old) => ({
                ...old,
                activeTab: 'open',
                page: 1,
              })}
              className={classNames(
                tab === 'open'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200',
                'whitespace-nowrap flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm',
              )}
              aria-current={tab === 'open' ? 'page' : undefined}
            >
              Open
              {count > 0 ? (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </Link>
            <Link
              search={(old) => ({
                ...old,
                activeTab: 'attended',
                page: 1,
              })}
              className={classNames(
                tab === 'attended'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200',
                'whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm',
              )}
              aria-current={tab === 'attended' ? 'page' : undefined}
            >
              Attended
            </Link>
          </nav>
        </div>
      </div>
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title="inbox"
            data={rows}
            columns={columns}
            loading={tableLoading}
            emptyDescription={
              tab === 'open'
                ? 'New orders land here automatically.'
                : 'Items you or the system have marked done will show up here.'
            }
            refetch={() => {
              refetch();
              if (tab === 'attended') refetchAttended();
            }}
            renderHeaderItems={() => (
              <select
                aria-label="Filter by kind"
                value={kindFilter ?? ''}
                onChange={(e) => setKind(e.target.value || undefined)}
                className="ml-3 h-[38px] rounded-md border-gray-300 pl-3 pr-8 text-sm text-gray-700 focus:border-primary-500 focus:ring-primary-500"
              >
                {kindFilterOptions.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            renderLoader={() => (
              <TableLoader items={[1, 1, 2, 1, 1]} showAction={true} />
            )}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                setOffset={setPage}
                totalItems={total}
                currentSize={rows.length}
              />
            )}
          />
        </div>
      </div>
    </main>
  );
};

export default InboxPage;
